import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { billingAPI } from '../api';

// Prepaid top-up that sits ON TOP of a subscription — not an alternative to
// one. The balance is only spent once the plan's included allowance runs out,
// so this screen deliberately shows what's included first and the balance
// second.
//
// The user types an amount in rupees and sees, right under the box, exactly how
// many messages it buys — the same number the server puts in the payment notes,
// so what they read is what they get.

const BuyCredits = () => {
  const [status, setStatus] = useState(null);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');
  const [paymentUrl, setPaymentUrl] = useState('');
  const [paymentLinkId, setPaymentLinkId] = useState('');
  const [waiting, setWaiting] = useState(false);
  const [checking, setChecking] = useState(false);
  const [success, setSuccess] = useState('');

  const navigate = useNavigate();
  const pollRef = useRef(null);
  const redirectRef = useRef(null);
  const creditsAtStart = useRef(0);

  const load = useCallback(async () => {
    try {
      const me = await billingAPI.me();
      setStatus(me);
      return me;
    } catch {
      setError('Could not load your balance. Please refresh.');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Clear both timers on unmount: a pending redirect firing after the user has
  // navigated away on their own would yank them somewhere they didn't ask for.
  useEffect(() => () => {
    clearInterval(pollRef.current);
    clearTimeout(redirectRef.current);
  }, []);

  const pricePaise = status?.creditPricePaise || 0;
  const minINR = status?.topUp?.minINR ?? 10;
  const maxINR = status?.topUp?.maxINR ?? 50000;

  const amountNum = Number(amount);
  const amountValid = Number.isFinite(amountNum) && amountNum >= minINR && amountNum <= maxINR;
  // Floor, matching the server — quoting a rounded-up number here would promise
  // a message the payment doesn't cover.
  const messages = amountValid && pricePaise > 0
    ? Math.floor((amountNum * 100) / pricePaise)
    : 0;

  // Razorpay tells our webhook, not this tab, so poll until the balance moves.
  //
  // Every tick re-reads our own balance (cheap). Every third tick ALSO asks the
  // server to check with Razorpay directly — that is what rescues a payment
  // whose webhook never arrives. It is spaced out because each one costs a
  // Razorpay API call, and the webhook usually wins the race anyway.
  const startPolling = (linkId) => {
    setWaiting(true);
    clearInterval(pollRef.current);
    const startedAt = Date.now();
    let tick = 0;

    pollRef.current = setInterval(async () => {
      tick += 1;

      if (linkId && tick % 3 === 0) {
        // A failure here is not fatal — the next tick tries again, and the
        // webhook may still land on its own.
        await billingAPI.verifyPayment(linkId).catch(() => {});
      }

      const me = await load();
      if (me && me.credits > creditsAtStart.current) {
        clearInterval(pollRef.current);
        onCreditsArrived(me.credits - creditsAtStart.current);
        return;
      }
      if (Date.now() - startedAt > 5 * 60 * 1000) {
        clearInterval(pollRef.current);
        setWaiting(false);
      }
    }, 4000);
  };

  // Credits landed. Confirm it, then go back to chat on its own — the user came
  // here from a blocked message, so the page they actually want is the one they
  // were thrown out of. The short pause is so the confirmation is readable
  // rather than a screen that flashes and vanishes.
  const onCreditsArrived = (added) => {
    setWaiting(false);
    setAmount('');
    setPaymentLinkId('');
    setSuccess(`${added} message${added === 1 ? '' : 's'} added. Taking you back to chat…`);
    redirectRef.current = setTimeout(() => navigate('/chat'), 1800);
  };

  // "I've paid, check now" — for the person who doesn't want to wait out the
  // polling interval, and for when polling has already given up.
  const checkNow = async () => {
    if (!paymentLinkId) return;
    setChecking(true);
    setError('');
    try {
      const r = await billingAPI.verifyPayment(paymentLinkId);
      const me = await load();
      if (me && me.credits > creditsAtStart.current) {
        clearInterval(pollRef.current);
        onCreditsArrived(me.credits - creditsAtStart.current);
      } else if (!r.paid) {
        setError("Razorpay hasn't recorded this payment yet. If you've just paid, try again in a few seconds.");
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Could not check the payment.');
    } finally {
      setChecking(false);
    }
  };

  const handleBuy = async () => {
    setError('');
    setStarting(true);
    creditsAtStart.current = status?.credits || 0;
    try {
      const res = await billingAPI.buyCredits(amountNum);
      setPaymentUrl(res.shortUrl);
      setPaymentLinkId(res.linkId || '');
      window.open(res.shortUrl, '_blank', 'noopener');
      startPolling(res.linkId);
    } catch (e) {
      setError(e.response?.data?.message || 'Could not start checkout. Please try again.');
    } finally {
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-black">
        <p className="text-gray-400">Loading…</p>
      </div>
    );
  }

  // A failed load leaves status null and pricePaise 0, which would fall through
  // to "Credits unavailable" below — blaming the pricing for what is actually a
  // dead backend. Report the real problem instead.
  if (!status) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-black px-4">
        <div className="max-w-md w-full bg-gray-800/50 rounded-2xl p-8 border border-gray-700/30 text-center">
          <h1 className="text-2xl font-bold text-white mb-3">Couldn't load your account</h1>
          <p className="text-gray-400 mb-6">{error || 'Please try again in a moment.'}</p>
          <button onClick={() => { setLoading(true); setError(''); load(); }}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-2.5 px-6 rounded-lg font-medium">
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Credits extend a plan, so a blocked account has to fix the plan first —
  // saying that here beats letting them fill the form and hit a 402.
  if (status && !status.allowed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-black px-4">
        <div className="max-w-md w-full bg-gray-800/50 rounded-2xl p-8 border border-gray-700/30 text-center">
          <h1 className="text-2xl font-bold text-white mb-3">Choose a plan first</h1>
          <p className="text-gray-400 mb-6">
            Usage credits top up an active plan — they aren't a plan on their own.
            Pick one, then you can add credits any time.
          </p>
          <button onClick={() => navigate('/subscribe')}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-2.5 px-6 rounded-lg font-medium">
            See plans
          </button>
        </div>
      </div>
    );
  }

  // Price of 0 means the admin hasn't priced credits, so there is nothing to sell.
  if (pricePaise <= 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-black px-4">
        <div className="max-w-md w-full bg-gray-800/50 rounded-2xl p-8 border border-gray-700/30 text-center">
          <h1 className="text-2xl font-bold text-white mb-3">Credits unavailable</h1>
          <p className="text-gray-400 mb-6">
            Usage credits can't be bought right now. You can upgrade your plan instead.
          </p>
          <button onClick={() => navigate('/subscribe')}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-2.5 px-6 rounded-lg font-medium">
            See plans
          </button>
        </div>
      </div>
    );
  }

  const rupeesEach = (pricePaise / 100).toFixed(2);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black px-4 py-10">
      <div className="max-w-md mx-auto">
        <h1 className="text-2xl font-bold text-white mb-1 text-center">Buy usage credits</h1>
        <p className="text-sm text-gray-400 mb-6 text-center">
          Money kept on your account, spent only after your plan's included messages
          run out. Credits don't expire and don't replace your plan.
        </p>

        <div className="bg-gray-800/50 rounded-2xl p-6 border border-gray-700/30 space-y-4">
          <div className="flex justify-between text-sm">
            <span className="text-gray-400">
              Plan {status?.planName && <span className="text-gray-300">({status.planName})</span>}
            </span>
            <span className="text-gray-200">
              {status?.usage?.limit > 0
                ? `${status.usage.used} / ${status.usage.limit} this month`
                : 'Unlimited'}
            </span>
          </div>
          {status?.usage?.dailyLimit > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Today</span>
              <span className="text-gray-200">
                {status.usage.usedToday} / {status.usage.dailyLimit} used
              </span>
            </div>
          )}
          <div className="flex justify-between text-sm border-b border-gray-700/50 pb-4">
            <span className="text-gray-400">Credit balance</span>
            <span className="text-teal-300 font-medium">{status?.credits || 0} messages</span>
          </div>

          <div>
            <label htmlFor="amount" className="block text-sm text-gray-300 mb-2">
              Amount (₹)
            </label>
            <input
              id="amount"
              type="number"
              min={minINR}
              max={maxINR}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={`${minINR} – ${maxINR}`}
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            />

            {/* The live quote, directly under the box */}
            <div className="mt-2 text-sm">
              {amount === '' ? (
                <span className="text-gray-500">₹{rupeesEach} per message</span>
              ) : amountValid ? (
                <span className="text-teal-300">
                  = <b>{messages.toLocaleString('en-IN')}</b> messages (₹{rupeesEach} each)
                </span>
              ) : (
                <span className="text-amber-400">
                  Enter an amount between ₹{minINR} and ₹{maxINR.toLocaleString('en-IN')}
                </span>
              )}
            </div>
          </div>

          {/* Quick picks — most people top up in round numbers */}
          <div className="flex gap-2">
            {[100, 500, 1000].filter(v => v >= minINR && v <= maxINR).map(v => (
              <button key={v} type="button" onClick={() => setAmount(String(v))}
                className="flex-1 py-2 rounded-lg bg-gray-700/60 hover:bg-gray-700 text-gray-200 text-sm">
                ₹{v}
              </button>
            ))}
          </div>

          {success && (
            <div className="p-3 bg-teal-500/10 border border-teal-500/40 rounded-lg text-center">
              <p className="text-teal-300 text-sm font-medium">✅ {success}</p>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {paymentLinkId && (
            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-center space-y-2">
              <p className="text-blue-300 text-sm">
                {waiting
                  ? 'Waiting for payment… your balance will update automatically.'
                  : "Finished paying? Check again — your balance will update."}
              </p>
              {paymentUrl && waiting && (
                <a href={paymentUrl} target="_blank" rel="noopener noreferrer"
                  className="block text-blue-400 hover:text-blue-300 text-sm underline">
                  Payment page didn't open? Click here
                </a>
              )}
              {/* Escape hatch: the automatic check runs every few seconds and
                  gives up after 5 minutes. This lets the user force it. */}
              <button
                type="button"
                onClick={checkNow}
                disabled={checking}
                className="text-sm px-3 py-1.5 rounded-md bg-blue-600/70 hover:bg-blue-600 text-white disabled:opacity-60"
              >
                {checking ? 'Checking…' : "I've paid — check now"}
              </button>
            </div>
          )}

          <button
            onClick={handleBuy}
            disabled={!amountValid || starting || waiting}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {starting ? 'Opening checkout…' : amountValid ? `Pay ₹${amountNum.toLocaleString('en-IN')}` : 'Pay'}
          </button>
        </div>

        <div className="mt-8 text-center space-x-6">
          <button onClick={() => navigate('/chat')} className="text-gray-400 hover:text-gray-200 text-sm">
            Back to chat
          </button>
          <button onClick={() => navigate('/subscribe')} className="text-gray-500 hover:text-gray-300 text-sm">
            Upgrade plan instead
          </button>
        </div>
      </div>
    </div>
  );
};

export default BuyCredits;
